<script setup lang="ts">
import { computed } from 'vue'
import { HANDOFF_LABEL } from '../../api/aimode/types'
import type { ContactTagRef, ConversationSummary, InboxSettings, InboxTab, LegacyInboxTab } from '../../api/types'
import type { StaffInfo } from '../../stores/inbox'
import { autoTagTitle } from '../../lib/autoTags'
import { ROW_TAGS, rowChips } from '../../lib/collab'
import { formatAgo, formatDuration, waitLevel, waitSeconds, WAIT_CLASS } from '../../lib/inbox'
import { AI_ROW_LABEL, aiRowState, BUSY_SHORT } from '../../lib/aiBusy'
import { blockedNow, chipTip } from '../../lib/wcblock'
import { QUICK_REPLY_LABEL, quickReply, quickReplyTitle } from '../../lib/style'
import { useAiMode } from '../../stores/aimode'
import { useCollab } from '../../stores/collab'
import { useInbox } from '../../stores/inbox'
import ChannelAvatar from '../ui/ChannelAvatar.vue'
import Icon from '../ui/Icon.vue'

const props = defineProps<{
  conv: ConversationSummary
  /** The list tab the row is shown in; null for search results. (Old keys needs_reply / waiting still accepted.) */
  tab: InboxTab | LegacyInboxTab | null
  now: number
  active: boolean
  settings: InboxSettings
  staff: StaffInfo
  /** Search hit text shown instead of the last message. */
  snippet?: string | null
  /**
   * The open chat moved to another list but stays here until you move on (stores/inbox.ts stickyNote):
   * "ตอบแล้ว · อยู่ในรอลูกค้าตอบ" in place of the preview, calm and muted.
   */
  moved?: string | null
  /** กำลังคุย › รอลูกค้าตอบ: nothing to do for us right now — a slightly calmer row. */
  quiet?: boolean
  /** AI tab: the list puts a "รับเรื่องเอง" button over the right end of the state line — keep that space free. */
  action?: boolean
}>()

defineEmits<{ open: [id: string] }>()

// the wait pill: chats waiting for OUR reply in กำลังคุย (not in the AI tab: the AI answers those)
const wait = computed(() =>
  props.tab === 'active' || props.tab === 'needs_reply' ? waitSeconds(props.conv, props.now) : null,
)
const level = computed(() => waitLevel(wait.value, props.settings))
const timeText = computed(() =>
  wait.value !== null ? formatDuration(wait.value) : formatAgo(props.conv.last_message_at, props.now),
)
const timeTip = computed(() => {
  if (wait.value === null) return 'ข้อความล่าสุด ' + timeText.value
  const t = 'รอตอบ ' + timeText.value
  return level.value === 'crit' ? `${t} · เกินเวลา ${Math.round(props.settings.reply_crit_sec / 60)} นาที` : t
})
const unread = computed(() => props.conv.unread > 0)
const outbound = computed(() => !props.snippet && props.conv.last_message?.direction === 'out')
const preview = computed(() => props.snippet ?? props.conv.last_message?.text ?? '')
/** "AI กำลังคิดคำตอบ…" in place of the preview while the AI works on this chat (lib/aiBusy.ts). */
const aimode = useAiMode()
const aiBusy = computed(() => aimode.busyOf(props.conv.id))
/** AI tab: what the AI is doing in this chat, in plain Thai (lib/aiBusy.ts aiRowState). */
const inAiTab = computed(() => props.tab === 'ai')
const aiState = computed(() => (inAiTab.value && props.conv.ai_mode ? aiRowState(props.conv, aiBusy.value) : null))
/** AI mode hand-off nobody picked up yet (docs/ai-mode.md): a warning chip in the first place. */
const handoff = computed(() => {
  const h = props.conv.ai_mode ? null : (props.conv.ai_handoff ?? null)
  return h ? (h.note?.trim() || HANDOFF_LABEL[h.code] || 'AI ส่งต่อ') : null
})
const assigned = computed(() => props.conv.assigned_staff)
/** Web chat visitor blocked by staff (lib/wcblock.ts): the first chip while the block runs. */
const blocked = computed(() => (blockedNow(props.conv.web_block, props.now) ? props.conv.web_block! : null))
/** แนวลูกค้า "ห้วน" waiting for our reply (lib/style.ts): "ตอบภายใน 5 นาที" in the chip place · warning after 5 minutes. */
const quick = computed(() => (props.moved ? null : quickReply(props.conv, props.now)))
// the customer's most important team labels (lib/collab.ts rowChips: up to 2, the rest "+N" — owner 18 ต.ค. 69 "ให้ติด tag
// รอโอน ด้วย และมี tag สำคัญต่างๆ") on a line of their own under the last message (owner 18 ต.ค. 69 "เอาไปยัดใส่ในบรรทัดเดียว
// มองไม่เห็นอะไรเลย"); a hand-off / block / ตอบภายใน 5 นาที chip stays before the message. A tag the system put on says why
// on hover (lib/autoTags.ts).
const chips = computed(() => (props.moved ? { shown: [], more: [] } : rowChips(props.conv, ROW_TAGS)))
const chipTitle = (t: ContactTagRef): string => autoTagTitle(t, props.now) ?? t.name
// colleagues in this chat right now (stores/collab.ts)
const collab = useCollab()
const inbox = useInbox()
const here = computed(() => collab.others(props.conv.id)[0] ?? null)
const hereTone = computed(() => (here.value ? inbox.staffInfo(here.value.staffId) : null))
const hereTip = computed(() =>
  here.value ? `${here.value.name}${here.value.state === 'typing' ? 'กำลังพิมพ์ตอบ' : ' กำลังดูแชทนี้'}` : '',
)
</script>

<template>
  <button
    class="row"
    :class="{
      'is-active': active,
      'has-unread': unread && !moved,
      'is-late': level === 'crit' && !moved,
      'is-moved': !!moved,
      'is-quiet': quiet && !moved,
    }"
    :aria-current="active ? 'true' : undefined"
    @click="$emit('open', conv.id)"
  >
    <!-- the channel logo is a badge on the picture (owner 27 ก.ย. 69), not before the name -->
    <ChannelAvatar
      :initial="conv.contact.initial"
      :url="conv.contact.avatar_url"
      :contact-id="conv.contact.id"
      :channel="conv.channel"
      :kind="conv.kind"
    />
    <span class="rb">
      <span class="rl1">
        <span class="nm">{{ conv.contact.name }}</span>
        <span v-if="unread && !moved" class="ud" title="ลูกค้าส่งข้อความใหม่ ยังไม่ได้เปิดอ่าน">ใหม่</span>
        <span
          v-if="conv.ai_mode && !inAiTab"
          class="aim"
          :class="{ busy: aiBusy }"
          :title="aiBusy ? BUSY_SHORT[aiBusy] : 'AI กำลังดูแลแชทนี้ · โดย ' + conv.ai_mode.by.name"
          ><Icon name="sparkle" />AI</span
        >
        <span v-if="conv.hot" class="hot" role="img" aria-label="น่าซื้อ" title="น่าซื้อ"><Icon name="flame" /></span>
        <span class="wt" :class="WAIT_CLASS[level]" :title="timeTip"
          ><Icon v-if="level !== 'ok'" name="clock" />{{ timeText }}</span
        >
      </span>
      <span v-if="moved" class="rl2 mv" :title="moved + ' · จะหายจากรายการนี้เมื่อเปิดแชทอื่น'">
        <span class="mv-ic"><Icon name="check" /></span>
        <span class="mv-t">{{ moved }}</span>
      </span>
      <span v-else class="rl2">
        <span v-if="handoff" class="tg tg-warn aih" :title="'AI ส่งต่อให้พนักงาน · ' + handoff"
          ><Icon name="handoff" />AI ส่งต่อ</span
        >
        <span v-else-if="blocked" class="tg tg-crit wb-chip" :title="chipTip(blocked, now)"><Icon name="minusCircle" />บล็อกอยู่</span>
        <span v-else-if="quick" class="tg qr" :class="{ late: quick.late }" :title="quickReplyTitle(quick)"
          ><Icon name="zap" />{{ QUICK_REPLY_LABEL }}</span
        >
        <span v-if="outbound" class="out-ic" title="ร้านตอบล่าสุด"><Icon name="reply" /></span>
        <span v-if="aiBusy && !snippet && !inAiTab" class="pv ai-busy">{{ BUSY_SHORT[aiBusy] }}</span>
        <span v-else class="pv">{{ preview }}</span>
        <span
          v-if="here && hereTone"
          class="pav"
          :class="['p-' + hereTone.tone, { typing: here.state === 'typing' }]"
          role="img"
          :aria-label="hereTip"
          :title="hereTip"
          >{{ hereTone.initial !== '·' ? hereTone.initial : here.name.slice(0, 1) }}</span
        >
        <span
          v-if="assigned"
          class="sav"
          :class="staff.tone"
          role="img"
          :aria-label="'ดูแลโดย ' + assigned.name"
          :title="'ดูแลโดย ' + assigned.name"
          >{{ staff.initial }}</span
        >
      </span>
      <!-- third line, only when there is something: the AI's state (AI tab) and the customer's tags -->
      <span v-if="(aiState || chips.shown.length) && !moved" class="rl3" :class="{ 'has-action': action, 'has-ais': aiState }">
        <span
          v-if="aiState"
          class="ais"
          :class="'ais-' + aiState"
          :title="`${AI_ROW_LABEL[aiState]} · ${conv.ai_mode?.by.name ?? ''} ให้ AI ดูแล`"
          ><span v-if="aiState === 'thinking' || aiState === 'typing'" class="ais-live" aria-hidden="true"></span
          ><Icon v-else name="sparkle" />{{ AI_ROW_LABEL[aiState] }}</span
        >
        <span v-for="t in chips.shown" :key="t.id" class="tg rtg" :class="'tg-' + t.tone" :title="chipTitle(t)">{{
          t.name
        }}</span>
        <span v-if="chips.more.length" class="tg-more" :title="chips.more.map((t) => t.name).join(' · ')">+{{ chips.more.length }}</span>
      </span>
    </span>
  </button>
</template>

<style scoped>
/* --ring: the row's own background, for the ring around the channel badge on the picture (ui/ChannelAvatar.vue) */
.row {
  --av-size: 40px;
  --ring: #fff;
  position: relative;
  flex: none;
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 12px 16px;
  border-bottom: 1px solid var(--line2);
  background: #fff;
}
.row:hover {
  background: #f8faf9;
  --ring: #f8faf9;
}
.row.is-active {
  background: var(--acs);
  --ring: var(--acs);
}
.row.is-active::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 3px;
  background: var(--ac);
}
/* 1280-wide screens: the list is 320 px — a slightly smaller picture keeps the whole name visible
   (both sizes 10% larger than before the channel badge moved onto the picture: 36 → 40, 32 → 35) */
@media (max-width: 1360px) {
  .row {
    --av-size: 35px;
    gap: 10px;
  }
}
/* text block next to the customer's picture */
.rb {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.rl1 {
  display: flex;
  align-items: center;
  gap: 8px;
  height: 22px;
  min-width: 0;
}
.rl2 {
  display: flex;
  align-items: center;
  gap: 8px;
  height: 20px;
  min-width: 0;
}
.nm {
  min-width: 0;
  font-size: 14px;
  font-weight: 500;
  line-height: 22px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.has-unread .nm {
  font-weight: 700;
}
.is-active .nm {
  font-weight: 600;
}
/* unread: solid green "ใหม่" pill (owner: a dot was too hard to see) */
.ud {
  flex: none;
  height: 18px;
  padding: 0 7px;
  border-radius: 9px;
  background: var(--ac);
  color: #fff;
  font-size: 12px;
  font-weight: 700;
  line-height: 18px;
  white-space: nowrap;
}
.hot {
  flex: none;
  display: inline-flex;
  color: #c2410c;
}
.hot :deep(.ic) {
  width: 15px;
  height: 15px;
  stroke-width: 2;
}
.wt {
  flex: none;
  margin-left: auto;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  line-height: 18px;
  color: var(--mut);
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}
.wt.w-warn,
.wt.w-crit {
  gap: 4px;
  height: 22px;
  padding: 0 8px 0 6px;
  border-radius: 6px;
  font-weight: 700;
}
.wt.w-warn {
  background: var(--warns);
  color: #7a4700;
}
.wt.w-crit {
  background: var(--crit);
  color: #fff;
}
.wt :deep(.ic) {
  width: 13px;
  height: 13px;
  stroke-width: 2.2;
}
/* over the reply limit: red edge on the row as well, so it shows while scanning the list */
.row.is-late::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 3px;
  background: var(--crit);
}
.out-ic {
  flex: none;
  display: inline-flex;
  color: #8a9a92;
}
.out-ic :deep(.ic) {
  width: 14px;
  height: 14px;
}
.pv {
  flex: 1 1 auto;
  min-width: 48px; /* a few words of the last message stay visible next to a hand-off / block chip */
  font-size: 13px;
  line-height: 20px;
  color: var(--mut);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.has-unread .pv {
  color: #2e3b35;
}
.tg {
  flex: none;
  padding: 0 6px;
  border-radius: 5px;
  font-weight: 600;
}
/* the customer's tags (up to 2, third line): the whole name; only a very long one ends in "…" (full name on hover) */
.tg.rtg {
  display: block; /* a block (not inline-flex) so a long name can end in "…" */
  flex: 0 1 auto;
  min-width: 32px;
  max-width: 160px;
  line-height: 20px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.tg-more {
  flex: none;
  font-size: 12px;
  font-weight: 600;
  line-height: 18px;
  color: var(--mut);
}
.pav {
  width: 20px;
  height: 20px;
}
/* AI mode (docs/ai-mode.md): small "AI" chip while the AI is in charge · warning chip after a hand-off */
.aim {
  flex: none;
  display: inline-flex;
  align-items: center;
  gap: 3px;
  height: 18px;
  padding: 0 6px 0 5px;
  border-radius: 9px;
  background: #e3f1ea;
  color: #0a5e47;
  font-size: 12px;
  font-weight: 700;
  line-height: 18px;
}
.aim :deep(.ic) {
  width: 11px;
  height: 11px;
}
/* the AI is thinking / typing in this chat: a gentle pulse, and the preview says so */
.aim.busy {
  animation: aim-pulse 1.6s ease-in-out infinite;
}
@keyframes aim-pulse {
  0%,
  100% {
    box-shadow: 0 0 0 0 rgba(14, 122, 92, 0.28);
  }
  50% {
    box-shadow: 0 0 0 4px rgba(14, 122, 92, 0);
  }
}
.pv.ai-busy {
  color: var(--ac2);
  font-style: italic;
}
@media (prefers-reduced-motion: reduce) {
  .aim.busy {
    animation: none;
    box-shadow: 0 0 0 2px rgba(14, 122, 92, 0.2);
  }
}
.aih {
  gap: 4px;
}
/* แนวลูกค้า "ห้วน" waiting for us (lib/style.ts): calm until 5 minutes, then the warning colour of the wait pill */
.qr {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  padding: 0 6px 0 5px;
  border: 1px solid #b9d7ca;
  background: #f1f8f4;
  color: #0a5e47;
  line-height: 18px;
  white-space: nowrap;
}
.qr.late {
  border-color: transparent;
  background: var(--warns);
  color: #7a4700;
}
.qr :deep(.ic) {
  width: 12px;
  height: 12px;
  stroke-width: 2.2;
}
/* web chat visitor blocked by staff (lib/wcblock.ts) */
.wb-chip {
  gap: 4px;
}
.wb-chip :deep(.ic) {
  width: 12px;
  height: 12px;
}
.aih :deep(.ic) {
  width: 12px;
  height: 12px;
}
/* third line: the customer's tags · AI tab: what the AI is doing first (lib/aiBusy.ts aiRowState) */
.rl3 {
  display: flex;
  align-items: center;
  gap: 6px;
  height: 20px;
  min-width: 0;
}
.rl3.has-ais {
  height: 24px;
}
.rl3.has-action {
  padding-right: 100px; /* the list's "รับเรื่องเอง" button sits over this end */
}
.ais {
  flex: 0 1 auto;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  min-width: 0;
  max-width: 100%;
  height: 22px;
  padding: 0 8px 0 7px;
  border-radius: 11px;
  background: #eef6f2;
  color: #24453a;
  font-size: 12px;
  font-weight: 600;
  line-height: 22px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.ais :deep(.ic) {
  flex: none;
  width: 12px;
  height: 12px;
  color: var(--ac);
}
/* thinking / typing: the AI is working right now — green, a softly breathing dot */
.ais-thinking,
.ais-typing {
  background: #e3f1ea;
  color: #0a5e47;
}
.ais-live {
  flex: none;
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: var(--ac);
  animation: ais-breathe 1.4s ease-in-out infinite;
}
@keyframes ais-breathe {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.35;
  }
}
/* the customer wrote, the AI has not started yet */
.ais-pending {
  background: #fdf6e7;
  color: #6b4a00;
}
.ais-pending :deep(.ic) {
  color: #b7791f;
}
/* answered, waiting for the customer: quiet */
.ais-answered,
.ais-watching {
  background: #f2f5f3;
  color: #43534b;
  font-weight: 500;
}
.ais-answered :deep(.ic),
.ais-watching :deep(.ic) {
  color: #7c8f86;
}
@media (prefers-reduced-motion: reduce) {
  .ais-live {
    animation: none;
  }
}
/* กำลังคุย › รอลูกค้าตอบ: nothing for us to do now — a touch calmer than the rows above */
.row.is-quiet:not(.is-active) {
  background: #fcfdfc;
  --ring: #fcfdfc;
}
.row.is-quiet .nm {
  color: #2e3b35;
  font-weight: 400;
}
/* the open chat already moved to another list (stays until you move on): muted, a small check */
.row.is-moved {
  background: #f6f8f7;
  --ring: #f6f8f7;
}
.row.is-moved.is-active::before {
  background: #9fb8ad;
}
.row.is-moved .nm {
  color: #3e4d46;
  font-weight: 500;
}
.row.is-moved :deep(.cha) {
  opacity: 0.7;
}
.mv {
  gap: 6px;
  color: var(--ac2);
}
.mv-ic {
  flex: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: #dcefe5;
}
.mv-ic :deep(.ic) {
  width: 11px;
  height: 11px;
  stroke-width: 2.6;
}
.mv-t {
  min-width: 0;
  font-size: 13px;
  line-height: 20px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.sav {
  flex: none;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  font-size: 12px;
  font-weight: 700;
  line-height: 20px;
  text-align: center;
}
</style>
